import { Box, Checkbox, Grid, MenuItem, Typography } from "@mui/material";
import { ReactElement } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import CommonTextField from "../../../common/components/functional/commonTextField/CommonTextField";
import FormSection from "../../../common/components/functional/formSection/FormSection";
import SalesPermissionStatusIndicator from "../common/SalesPermissionStatusIndicator";
import SalesPermissionText from "../common/SalesPermissionText";

type GridContainerProps = {
  children: ReactElement;
  container?: boolean;
};

const GridContainer = ({ container, children }: GridContainerProps) =>
  container ? (
    <Grid container item xs={2.8} justifyContent="flex-end" alignItems="center">
      {children}
    </Grid>
  ) : (
    <Grid item xs={9.2}>
      {children}
    </Grid>
  );

type CompanyBasicInfoProps = {
  companyClassificationIsEnabled: boolean;
};

const CompanyBasicInfo = ({
  companyClassificationIsEnabled,
}: CompanyBasicInfoProps) => {
  const { control } = useFormContext();

  const { t } = useTranslation();

  return (
    <FormSection title={t("customers:companyInfo:companyBasicInfo")} margin>
      <GridContainer container>
        <Typography variant="secondary">{t("customers:businessId")}</Typography>
      </GridContainer>
      <GridContainer>
        <Controller
          name="businessId"
          control={control}
          render={({ field }) => <Typography>{field.value}</Typography>}
        />
      </GridContainer>
      <GridContainer container>
        <Typography variant="secondary">
          {t("customers:customerNumber")}
        </Typography>
      </GridContainer>
      <GridContainer>
        <Controller
          name="customerNumber"
          control={control}
          render={({ field }) => <Typography>{field.value}</Typography>}
        />
      </GridContainer>
      <GridContainer container>
        <Typography variant="secondary">{t("common:street")}</Typography>
      </GridContainer>
      <GridContainer>
        <Controller
          name="street"
          control={control}
          render={({ field }) => <Typography>{field.value}</Typography>}
        />
      </GridContainer>
      <GridContainer container>
        <Typography variant="secondary" textAlign="right">
          {t("common:zipCodeAndCity")}
        </Typography>
      </GridContainer>
      <GridContainer>
        <Controller
          name="zipCodeAndCity"
          control={control}
          render={({ field }) => <Typography>{field.value}</Typography>}
        />
      </GridContainer>
      <GridContainer container>
        <Typography variant="secondary">
          {t("customers:nationwideCustomer")}
        </Typography>
      </GridContainer>
      <GridContainer>
        <Controller
          name="isNationwide"
          control={control}
          render={({ field }) => (
            <Checkbox
              {...field}
              sx={{ padding: 0 }}
              checked={field.value}
              disabled={!companyClassificationIsEnabled}
            />
          )}
        />
      </GridContainer>
      <GridContainer container>
        <Typography variant="secondary">
          {t("customers:companyInfo:fieldOfWork")}
        </Typography>
      </GridContainer>
      <GridContainer>
        <Controller
          name="industry"
          control={control}
          render={({ field }) => (
            <CommonTextField
              disabled
              {...field}
              sx={{ maxWidth: "372px" }}
              fullWidth
              size="small"
              defaultValue={field.value}
              value={field.value}
              select
            >
              <MenuItem value={field.value}>{field.value}</MenuItem>
            </CommonTextField>
          )}
        />
      </GridContainer>
      <GridContainer container>
        <Typography variant="secondary">
          {t("customers:salesPermission")}
        </Typography>
      </GridContainer>
      <GridContainer>
        <Controller
          name="salesPermission"
          control={control}
          render={({ field: { value } }) => (
            <Box display="flex" alignItems="center" gap="15px">
              <SalesPermissionStatusIndicator
                riskLevel={value["riskLevel"]}
                larger
              />
              <SalesPermissionText
                verificationDate={value["verificationDate"]}
              />
            </Box>
          )}
        />
      </GridContainer>
    </FormSection>
  );
};

export default CompanyBasicInfo;
